import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, History, Loader2, CheckCircle2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { isHrvQuestionnaire } from "@/lib/questionnaireType";
import { toast } from "sonner";

export type AssessmentVersionRow = {
  id: string;
  version_number: number;
  status: string;
  is_latest: boolean;
  created_at: string;
  submitted_at: string | null;
  reviewed_at: string | null;
  parent_assessment_id: string | null;
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  portfolioId: string | null;
  questionnaireId: string | null;
  clientName?: string;
}

const COMPLETED_STATUSES = ["Submitted", "Reviewed", "Locked", "Closed"];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function statusVariant(status: string, isLatest: boolean) {
  const isCompleted = COMPLETED_STATUSES.includes(status);
  if (isCompleted) {
    return {
      label: status === "Submitted" ? "Completed" : status,
      className: "bg-green-100 text-green-700 border-green-200",
      icon: <CheckCircle2 className="h-3 w-3 mr-1" />,
    };
  }
  if (status === "Draft" || status === "In Progress" || status === "In Review") {
    return {
      label: isLatest ? "In Progress" : status,
      className: "bg-blue-100 text-blue-700 border-blue-200",
      icon: <Clock className="h-3 w-3 mr-1" />,
    };
  }
  return {
    label: status,
    className: "bg-muted text-muted-foreground",
    icon: null,
  };
}

export function AssessmentVersionHistoryModal({
  open,
  onOpenChange,
  portfolioId,
  questionnaireId,
  clientName,
}: Props) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState<AssessmentVersionRow[]>([]);

  useEffect(() => {
    if (!open || !portfolioId || !questionnaireId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await (supabase as any).rpc(
          "pm_get_assessment_versions",
          { p_org_id: portfolioId, p_questionnaire_id: questionnaireId }
        );
        if (error) throw error;
        if (!cancelled) setVersions((data as AssessmentVersionRow[]) ?? []);
      } catch (e: any) {
        console.error("[VersionHistory] error", e);
        if (!cancelled) toast.error(e?.message || "Failed to load history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, portfolioId, questionnaireId]);

  const handleOpenReport = (v: AssessmentVersionRow) => {
    const path = isHrvQuestionnaire(questionnaireId)
      ? `/reports/hrv/${v.id}`
      : `/reports/self/${v.id}`;
    navigate(path);
    onOpenChange(false);
  };

  const handleResume = (v: AssessmentVersionRow) => {
    navigate(`/wizard/${v.id}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Assessment History
          </DialogTitle>
          <DialogDescription>
            All versions of this assessment{clientName ? ` for ${clientName}` : ""}. Historical versions are read-only.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading history…
          </div>
        ) : versions.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            No assessment history yet.
          </div>
        ) : (
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {versions.map((v) => {
              const sv = statusVariant(v.status, v.is_latest);
              const isCompleted = COMPLETED_STATUSES.includes(v.status);
              return (
                <div
                  key={v.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">Version {v.version_number}</span>
                      {v.is_latest && (
                        <Badge variant="outline" className="text-[10px]">Latest</Badge>
                      )}
                      <Badge className={`text-[10px] font-medium border ${sv.className}`}>
                        {sv.icon}
                        {sv.label}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Created {formatDate(v.created_at)}
                      {v.submitted_at ? ` · Submitted ${formatDate(v.submitted_at)}` : ""}
                    </div>
                  </div>
                  {isCompleted ? (
                    <Button size="sm" variant="outline" onClick={() => handleOpenReport(v)}>
                      <FileText className="h-3.5 w-3.5 mr-1.5" />
                      Open Report
                    </Button>
                  ) : v.is_latest ? (
                    <Button size="sm" variant="outline" onClick={() => handleResume(v)}>
                      Resume
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground italic">Abandoned</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
